import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { MEDICATION_MODES, MedicationMode } from "@/modules/patients/detail/medication/types/medication-mode"

interface ChMedicationStoreState {
  chMedicationMode: MedicationMode
  prevChMode: MedicationMode
  existingMedication: chMedicationFragmentInline$data | null
  setChMedicationDrawerMode: (mode: MedicationMode) => void
  chooseSearchItem: () => void
  reset: () => void
  selectExistingMedication: (existingMedication: chMedicationFragmentInline$data) => void
}

const initialState = {
  chMedicationMode: null,
  prevChMode: null,
  existingMedication: null,
}

function createChMedicationStore() {
  return create<ChMedicationStoreState>((set, get) => ({
    ...initialState,
    setChMedicationDrawerMode: (mode) =>
      set((state) => ({
        chMedicationMode: mode,
        prevChMode: !mode && state.chMedicationMode === MEDICATION_MODES.VIEW ? null : state.chMedicationMode,
      })),
    chooseSearchItem: () => {
      const { prevChMode, chMedicationMode } = get()
      let prevMode: MedicationMode = null
      if (
        (prevChMode === MEDICATION_MODES.VIEW && chMedicationMode !== MEDICATION_MODES.VIEW) ||
        chMedicationMode === MEDICATION_MODES.VIEW
      ) {
        prevMode = MEDICATION_MODES.VIEW
      }
      if (prevChMode === MEDICATION_MODES.CREATION && chMedicationMode === MEDICATION_MODES.CREATION) {
        prevMode = null
      }
      set(() => ({
        chMedicationMode: MEDICATION_MODES.CREATION,
        prevChMode: prevMode,
      }))
    },
    reset() {
      set(initialState)
    },
    selectExistingMedication: (existingMedication) =>
      set(() => ({
        chMedicationMode: MEDICATION_MODES.EDIT,
        prevChMode: MEDICATION_MODES.VIEW,
        existingMedication,
      })),
  }))
}

const ChMedicationContext = createContext<ReturnType<typeof createChMedicationStore> | null>(null)

type ChMedicationProviderProps = React.PropsWithChildren

export const ChMedicationProvider = ({ children }: ChMedicationProviderProps) => {
  const storeRef = useRef<ReturnType<typeof createChMedicationStore>>(undefined)
  if (!storeRef.current) {
    storeRef.current = createChMedicationStore()
  }
  return <ChMedicationContext.Provider value={storeRef.current}>{children}</ChMedicationContext.Provider>
}

export function useChMedicationContext<T>(selector: (state: ChMedicationStoreState) => T): T {
  const store = useContext(ChMedicationContext)
  if (!store) throw new Error("Missing ChMedicationProvider in the tree")
  return useStore(store, selector)
}
